import { defined } from '@convincely/utils/lib/defined.js';
import { get } from '@convincely/utils/lib/object.js';
import { Store } from '../../models/Store.js';
/**
 * A wrapper for items object used throughout the core framework
 *
 * @augments {Store.<DataItem>}
 * @template T
 */
export class DataItems extends Store {
    /**
     * @param {Array.<DataItem|object>} [items] - list of initial items
     * @param {string}     [key]   - key used for mapping items
     * @param {string}     [label] - key used for mapping labels
     */
    constructor(items = [], key = 'value', label = 'label') {
        super();
        this.getByKey = this.getByKey.bind(this);
        this.key = key;
        this.label = label;
        this.list = items;
        this.original = items.slice(0);
        this.sync();
        this.setStore(items);
        this.originalMap = Object.assign({}, this.map);
    }
    /**
     * @param {DataItem} item - item to add or replace if exists
     */
    add(item) {
        this.insert(item);
    }
    /**
     * @param {string} key - which key to sort by
     * @param {boolean} [directionAsc] - which direction to sort
     */
    sort(key, directionAsc = true) {
        this.sortKey = key;
        const newList = this.list.slice(0);
        if (directionAsc) {
            newList.sort(this._sortAsc);
        }
        else {
            newList.sort(this._sortDesc);
        }
        this.set(newList);
    }
    _sortAsc = (a, b) => get(a, this.sortKey, '')?.localeCompare(get(b, this.sortKey, ''));
    _sortDesc = (a, b) => get(b, this.sortKey, '')?.localeCompare(get(a, this.sortKey, ''));
    /**
     * @param {...(DataItem|string)} items - item to add or replace if exists
     */
    addOriginal(...items) {
        for (const value of items) {
            const item = this.resolveItem(value, this.originalMap);
            if (this.list.indexOf(item) < 0) {
                const insertIndex = this.getPreviousIndex(this.original.indexOf(item));
                this.map[item[this.key]] = item;
                this.list.splice(insertIndex + 1, 0, item);
            }
        }
        this.set(this.list.slice(0));
    }
    /**
     * @param {number} originalIndex
     * @param {number} offset
     * @return {number}
     */
    getPreviousIndex(originalIndex, offset = 1) {
        const beforeIndex = this.list.indexOf(this.original[originalIndex - offset]);
        return beforeIndex < 0 && originalIndex - offset > -1 ? this.getPreviousIndex(originalIndex, offset + 1) : beforeIndex;
    }
    /**
     * @param {DataItem} item - item to prepend or replace if exists
     */
    prepend(item) {
        this.insert(item, false);
    }
    /**
     * @private
     * @param {DataItem} item - item to insert or replace if exists
     * @param {boolean} atEnd - flag to insert item at the end or start of the list
     */
    insert(item, atEnd = true) {
        const current = this.getByKey(item[this.key]);
        this.map[item[this.key]] = item;
        if (current) {
            this.list[this.list.indexOf(current)] = item;
            this.set(this.list.slice(0));
        }
        else {
            if (atEnd) {
                this.list.push(item);
            }
            else {
                this.list.unshift(item);
            }
            this.set(this.list.slice(0));
        }
    }
    /**
     * @param {DataItem|string} value - item to remove
     * @param {DataItem} [addItem] - item to add
     * @param {InputItem} [store]
     */
    remove(value, addItem, store) {
        const item = this.resolveItem(value);
        const index = this.list.indexOf(item);
        if (index > -1) {
            delete this.map[item[this.key]];
            if (addItem) {
                this.list.splice(index, 1, addItem);
                this.map[addItem[this.key]] = addItem;
            }
            else {
                this.list.splice(index, 1);
            }
            if (store) {
                store.remove();
            }
            this.set(this.list.slice(0));
        }
    }
    /**
     * @param {DataItem | string} value
     * @param {object} map
     * @return {DataItem}
     */
    resolveItem(value, map = this.map) {
        return typeof value === 'string' ? map[value] : value;
    }
    /**
     * @param {Array.<DataItem> | Array.<object>} [items] - list of initial items
     * @param {boolean} [isOriginal]
     */
    init(items, isOriginal) {
        this.list = items;
        this.sync();
        super.set(items);
        if (isOriginal) {
            this.original = items.slice(0);
            this.originalMap = Object.assign({}, this.map);
        }
    }
    /**
     * @param {DataItem} first
     * @param {DataItem} second
     */
    swap(first, second) {
        const firstIndex = this.list.indexOf(first);
        const secondIndex = this.list.indexOf(second);
        if (secondIndex > firstIndex) {
            this.list[firstIndex] = second;
            this.list[secondIndex] = first;
            this.set(this.list.slice(0));
        }
    }
    /**
     * @param {Array.<DataItem>} value
     */
    set(value) {
        this.list = value;
        super.set(value);
    }
    sync() {
        const { key, list: items } = this;
        const map = this.map = {};
        const keys = this.keys = [];
        if (Array.isArray(items)) {
            items.forEach((item, index) => {
                if (!defined(item[key])) {
                    item[key] = index;
                }
                const name = item[key];
                map[name] = item;
                keys[index] = name;
            });
        }
    }
    /**
     * @param {string} key
     * @return {DataItem}
     */
    getByKey(key) {
        return this.map[key];
    }
    /**
     * @param {DataItem} item
     * @param {Array.<DataItem>} [list]
     * @return {number}
     */
    getIndex(item, list = this.list) {
        return list.indexOf(this.getByKey(item[this.key]));
    }
    /**
     * @param {string} key
     * @param {Array.<DataItem>} [list]
     * @return {number}
     */
    getIndexByKey(key, list = this.list) {
        return list.indexOf(this.getByKey(key));
    }
    /**
     * @param {DataItem} item
     * @param {Array.<DataItem>} [list]
     * @return {DataItem}
     */
    getPrevious(item, list = this.list) {
        return list[this.getIndex(item, list) - 1];
    }
    /**
     * @param {DataItem} item
     * @param {Array.<DataItem>} [list]
     * @return {DataItem}
     */
    getNext(item, list = this.list) {
        return list[this.getIndex(item, list) + 1];
    }
    /**
     * Returns the first value of items list
     *
     * @return {*}
     */
    get firstValue() {
        return this.list?.[0]?.[this.key];
    }
}
//# sourceMappingURL=DataItems.js.map